<template>
  <div class="profile-relationships">
    <section class="parents-area">
      <h5>Parents</h5>
      <ul v-if="visibleParentViewFamiliesRef.length" class="list-unstyled parents-list">
        <li v-for="pf in visibleParentViewFamiliesRef" :key="pf.family.id">
          <ProfileParentRelationship
            :child-id="props.personId"
            :parent-family="pf"
            :owner-only="props.ownerOnly"
            @add-sibling="onAddSibling(pf.family)"
            @change="onChangeParents"
            @remove="onRemoveParents">
          </ProfileParentRelationship>
        </li>
      </ul>
      <div v-if="props.ownerOnly && hasOtherParentFamiliesRef" class="alert alert-light alert-xs other-rels-alert">
        <template v-if="visibleParentViewFamiliesRef.length">
          Another set of parents are defined in a matching profile.
        </template>
        <template v-else>
          Parents are defined in a matching profile.
        </template>
      </div>
      <div v-if="!visibleParentViewFamiliesRef.length && !canEditRef" class="no-parents">
        None
      </div>
      <div v-if="canEditRef" class="action-row">
        <button type="button" class="btn btn-inline btn-link action" @click="addParents">
          Add parents
        </button>
      </div>
    </section>
    <section class="spouses-area">
      <h5>Spouses</h5>
      <ul v-if="visibleSpouseViewFamiliesRef.length" class="list-unstyled spouses-list">
        <li v-for="sf in visibleSpouseViewFamiliesRef" :key="sf.family.id">
          <ProfileSpouseRelationship
            :person-id="props.personId"
            :spouse-family="sf"
            :owner-only="props.ownerOnly"
            v-model:expanded-id="expandedFamilyIdRef"
            @add-child="onAddChild(sf.family)"
            @remove="onRemoveSpouse(sf)">
          </ProfileSpouseRelationship>
        </li>
      </ul>
      <div v-if="props.ownerOnly && hasOtherSpouseFamiliesRef" class="alert alert-light alert-xs other-rels-alert">
        <template v-if="visibleSpouseViewFamiliesRef.length">
          Another spouse is defined in a matching or related profile.
        </template>
        <template v-else>
          A spouse is defined in a matching or related profile.
        </template>
      </div>
      <div v-if="!visibleSpouseViewFamiliesRef.length && !canEditRef" class="no-spouses">
        None
      </div>
      <div v-if="canEditRef" class="action-row">
        <button type="button" class="btn btn-inline btn-link action" @click="addSpouse">
          Add a spouse
        </button>
      </div>
    </section>
    <ProfileAddRelatedModal ref="addRelatedModal" @added="emit('reload')"/>
    <ProfileSpouseRemoveModal ref="removeSpouseModalRef"/>
    <ProfileChildRemoveModal ref="removeChildModalRef" @removed="emit('reload')"/>
    <ProfileAddParentsModal ref="addParentsModalRef" @added="emit('reload')"/>
  </div>
</template>

<style lang="scss" scoped>
.profile-relationships,
.parents-area,
.spouses-area {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
  
h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: normal;
}

.other-rels-alert {
  margin: 0;
  margin-left: 1rem;
  //font-size: 0.75rem;
  //font-style: italic;
  //color: #888;
}

.no-spouses, .no-parents {
  margin-left: 1rem;
  font-size: 0.875rem;
  color: #aaa;
}

.action-row {
  margin-left: 1rem;
  display: flex;
  gap: 1rem;

  .action {
    font-size: 0.875rem;
  }
}

.spouses-list {
  margin: 0;
  margin-left: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.parents-list {
  margin: 0;
  margin-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

label {
  font-size: 0.875rem;
  color: #888;

  input[type="checkbox"] {
    margin-right: 0.5rem;
  }
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Gender, ViewFamily } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { FamilyAndChildren, useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { CompositeId } from '@/rd/CompositeId'
import { NewProfileRelationship } from './ProfileManager'
import _, { sortBy } from 'lodash'
import ProfileSpouseRelationship from './ProfileSpouseRelationship.vue'
import ProfileParentRelationship from './ProfileParentRelationship.vue'
import ProfileAddRelatedModal from './ProfileAddRelatedModal.vue'
import ProfileSpouseRemoveModal from './ProfileSpouseRemoveModal.vue'
import ProfileChildRemoveModal from './ProfileChildRemoveModal.vue'
import ProfileAddParentsModal from './ProfileAddParentsModal.vue'

const props = defineProps<{
  personId: string,
  // If true, only show relationships defined in this profile (not matching profiles)
  ownerOnly?: boolean,
}>()

const emit = defineEmits([
  'reload',
])

const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const personStore = usePersonStore()
const dataGroupStore = useDataGroupStore()

const personGroupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const personRef = computed(() => personStore.getAsyncPerson(props.personId)?.data)
const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const allSpouseViewFamiliesRef = computed(() => {
  const vpId = viewPersonRef.value?.id // use view person id to get related families
  if (!vpId)
    return []

  const spouseViewFamilies = viewFamilyStore.getLoadedSpouseFamilies(vpId).spouseFamilies
  const spouseFamiliesAndChildren: FamilyAndChildren[] = []
  for (const sf of spouseViewFamilies) {
    const children = viewFamilyStore.getLoadedChildren(sf.id!).children
    spouseFamiliesAndChildren.push({
      family: sf,
      childIds: children.map(ch => ch.id!),
    })
  }
  return sortBy(spouseFamiliesAndChildren, f => !isOwnerFamily(f)) // profile families first
})
const ownerSpouseViewFamiliesRef = computed(() => allSpouseViewFamiliesRef.value.filter(isOwnerFamily))
const visibleSpouseViewFamiliesRef = computed(() => 
  props.ownerOnly ? ownerSpouseViewFamiliesRef.value : allSpouseViewFamiliesRef.value)
const hasOtherSpouseFamiliesRef = computed(() => 
  allSpouseViewFamiliesRef.value.length > ownerSpouseViewFamiliesRef.value.length)

const allParentViewFamiliesRef = computed(() => {
  const vpId = viewPersonRef.value?.id // use view person id to get related families
  if (!vpId)
    return []

  const parentViewFamilies = viewPersonStore.getLoadedParentFamilies(vpId).parentFamilies
  const parentFamAndChildren: FamilyAndChildren[] = []
  for (const pf of parentViewFamilies) {
    const children = viewFamilyStore.getLoadedChildren(pf.id!).children
    parentFamAndChildren.push({
      family: pf,
      childIds: children.map(ch => ch.id!),
    })
  }
  return sortBy(parentFamAndChildren, f => !isOwnerFamily(f)) // owned families first
})
const ownerParentViewFamiliesRef = computed(() => allParentViewFamiliesRef.value.filter(isOwnerFamily))
const visibleParentViewFamiliesRef = computed(() => 
  props.ownerOnly ? ownerParentViewFamiliesRef.value : allParentViewFamiliesRef.value)
const hasOtherParentFamiliesRef = computed(() => 
  allParentViewFamiliesRef.value.length > ownerParentViewFamiliesRef.value.length)

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(personGroupIdRef.value)?.data ?? ItemPermissions.None)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)

const expandedFamilyIdRef = ref<string>()
const addRelatedModal = ref<InstanceType<typeof ProfileAddRelatedModal>>()
const removeSpouseModalRef = ref<InstanceType<typeof ProfileSpouseRemoveModal>>()
const removeChildModalRef = ref<InstanceType<typeof ProfileChildRemoveModal>>()
const addParentsModalRef = ref<InstanceType<typeof ProfileAddParentsModal>>()

function isOwnerFamily(f: FamilyAndChildren) {
  return f.family.matchIds.some(id => CompositeId.hasGroupId(id, personGroupIdRef.value!))
}

function addSpouse() {
  const personGender = personRef.value?.displayProperties.gender
  if (!personGender) {
    alert('Please enter the gender for this profile before adding a spouse.')
    return
  }

  const spouseRel = personGender == Gender.Male
    ? NewProfileRelationship.Wife
    : NewProfileRelationship.Husband

  addRelatedModal.value?.open(spouseRel, props.personId)
}

function onAddChild(spouseFamily: ViewFamily) {
  addRelatedModal.value?.open(NewProfileRelationship.Son, spouseFamily.id!)
}

function onRemoveSpouse(spouseFamily: FamilyAndChildren) {
  removeSpouseModalRef.value?.open(props.personId, spouseFamily)
}

function onAddSibling(parentFamily: ViewFamily) {
  addRelatedModal.value?.open(NewProfileRelationship.Son, parentFamily.id!)
}

function addParents() {
  addParentsModalRef.value?.open(props.personId)
}

function onChangeParents() {
  //
}

function onRemoveParents(parentFamily: ViewFamily) {
  removeChildModalRef.value?.open(props.personId, parentFamily, true)
}

// list matches
// match/unmatch
// show person sets and owners
// don't confuse matches and profile relationships (match placeholder to primary)

// show view spouse?
// show direct spouse relationships (defined via placeholder match by this or the other profile)
// show family details somehow?
// add/remove spouses
// ISSUE: spouse family is replicated in child profiles, so all copies would need to be "disputed"

// show direct parent relationships (defined via placeholder match in this profile)
// add/remove parent relationships

</script>
